import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import BillingPage from '../Payments/BillingPage';
import PlansPage from '../Register/PlansPage';
import { Actions as PAYMENTACTIONS } from '../../store/actions/paymentActions';

/**
 * Plan and Billing (R-6073): current plan first; catalog only when changing plan or none.
 */
const PlanAndBilling = ({
  orgDetails,
  cyfAdmin,
  changeCatalogPlan,
  getLedgerInvoices,
  getPriceSaveOffers,
  connectLedgerXero,
  getLedgerXeroStatus,
}) => {
  const currentPlan = orgDetails?.planId || orgDetails?.subscriptionPlan || null;
  const [changingPlan, setChangingPlan] = useState(!currentPlan);
  const [ledgerInvoices, setLedgerInvoices] = useState([]);
  const [priceSave, setPriceSave] = useState(null);
  const [ledgerXero, setLedgerXero] = useState({ connected: false });
  const [xeroBusy, setXeroBusy] = useState(false);

  useEffect(() => {
    getLedgerInvoices().then(setLedgerInvoices);
    getPriceSaveOffers().then(setPriceSave);
    getLedgerXeroStatus().then(setLedgerXero);
  }, [orgDetails?.id]);

  const handleDowngrade = async (nextPlanId) => {
    await changeCatalogPlan({ nextPlanId });
  };

  const handlePriceSave = async (offer) => {
    await changeCatalogPlan({ priceSaveOffer: offer });
  };

  const handleConnectXero = async () => {
    setXeroBusy(true);
    try {
      await connectLedgerXero();
    } finally {
      setXeroBusy(false);
    }
  };

  return (
    <div className='mx-auto max-w-6xl space-y-6 p-4'>
      <Card className='bg-card text-card-foreground'>
        <CardHeader className='bg-header text-header-foreground'>
          <CardTitle className='font-semibold'>Plan and Billing</CardTitle>
        </CardHeader>
        <CardContent className='space-y-4 pt-4'>
          {currentPlan ? (
            <p className='text-foreground'>
              Current plan: <span className='font-semibold'>{currentPlan}</span>
              {orgDetails?.fteBand ? <span className='text-muted-foreground'> · {orgDetails.fteBand} FTE</span> : null}
            </p>
          ) : (
            <p className='text-muted-foreground'>No catalog plan is in force yet.</p>
          )}
          {orgDetails?.scheduledPlanId && (
            <p className='text-sm text-muted-foreground'>
              Scheduled change to {orgDetails.scheduledPlanId} on {orgDetails.scheduledPlanStart}.
            </p>
          )}
          <div className='rounded border border-border p-3 space-y-2'>
              <p className='text-sm font-semibold text-foreground'>CYF ledger Xero (Demo Company)</p>
              {ledgerXero.connected ? (
                <p className='text-sm text-muted-foreground'>
                  Connected: {ledgerXero.tenantName || ledgerXero.tenantId}
                  {ledgerXero.expires ? ` · access expires ${ledgerXero.expires}` : ''}
                </p>
              ) : (
                <p className='text-sm text-muted-foreground'>
                  Not connected. Log in with Xero and choose the Demo Company (never production books).
                </p>
              )}
              <Button variant='outline' size='sm' loading={xeroBusy} onClick={handleConnectXero}>
                {ledgerXero.connected ? 'Reconnect ledger Xero' : 'Connect ledger Xero'}
              </Button>
            </div>
          {currentPlan && !changingPlan && (
            <Button variant='outline' size='sm' onClick={() => setChangingPlan(true)}>
              Change plan
            </Button>
          )}
          {currentPlan && changingPlan && (
            <Button variant='ghost' size='sm' onClick={() => setChangingPlan(false)}>
              Keep current plan
            </Button>
          )}
          {currentPlan && currentPlan !== 'discover' && (
            <Button variant='text' size='sm' onClick={() => handleDowngrade('discover')}>
              Schedule downgrade after this term
            </Button>
          )}
          {priceSave?.allowed && (
            <div className='flex flex-wrap gap-2'>
              {priceSave.offers.includes('conversation') && (
                <Button variant='outline' size='sm' onClick={() => handlePriceSave('conversation')}>
                  Talk about price
                </Button>
              )}
              {priceSave.offers.includes('ten_percent_next_year') && (
                <Button variant='outline' size='sm' onClick={() => handlePriceSave('ten_percent_next_year')}>
                  10% off next year (once)
                </Button>
              )}
            </div>
          )}
          <p className='text-sm text-muted-foreground'>
            Invoices and payment methods stay on this page.{' '}
            <Link className='text-primary underline' to='/addons'>
              Add-ons
            </Link>
          </p>
          {ledgerInvoices.length > 0 && (
            <ul className='text-sm text-muted-foreground space-y-1'>
              {ledgerInvoices.slice(0, 6).map((inv) => (
                <li key={inv.id}>
                  {inv.periodStart} · {inv.currency} {inv.gross} · {inv.status}
                  {inv.xeroInvoiceId ? ' · Xero' : inv.xeroStatus === 'unposted' ? ' · unposted' : ''}
                  {inv.purchaseOrderReference ? ` · PO ${inv.purchaseOrderReference}` : ''}
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
      {(changingPlan || !currentPlan) && <PlansPage />}
      <BillingPage />
    </div>
  );
};

const mapStateToProps = (state) => ({
  orgDetails: state.organisation?.details,
  cyfAdmin: !!state.profile?.details?.cyfAdmin,
});

const mapDispatchToProps = (dispatch) => ({
  changeCatalogPlan: (payload) => dispatch(PAYMENTACTIONS.changeCatalogPlan(payload)),
  getLedgerInvoices: () => dispatch(PAYMENTACTIONS.getLedgerInvoices()),
  getPriceSaveOffers: () => dispatch(PAYMENTACTIONS.getPriceSaveOffers()),
  connectLedgerXero: () => dispatch(PAYMENTACTIONS.connectLedgerXero()),
  getLedgerXeroStatus: () => dispatch(PAYMENTACTIONS.getLedgerXeroStatus()),
});

export default connect(mapStateToProps, mapDispatchToProps)(PlanAndBilling);
